import { useState } from "react";

import { api } from "../lib/api";
import type { Lane, TestRun, Watch } from "../lib/types";
import { confidenceClass, money } from "./MatchCard";

/**
 * The tuning loop: dry-run criteria against stored listings and read the reasons.
 * It runs the *unsaved* editor text, so you can try a wording change before committing it.
 */
export default function TestPanel({
  watch,
  draftCriteria,
  draftSchemaText,
  draftMinConfidence,
  draftMaxPrice,
  lanes,
}: {
  watch: Watch;
  draftCriteria: string;
  draftSchemaText: string;
  draftMinConfidence: number;
  draftMaxPrice: number | null;
  lanes: Lane[];
}) {
  const [days, setDays] = useState(7);
  const [limit, setLimit] = useState(40);
  const [laneId, setLaneId] = useState<number | "">(watch.lane_id ?? "");
  const [includeRules, setIncludeRules] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [run, setRun] = useState<TestRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function test() {
    setBusy(true);
    setError(null);
    try {
      let schema: Record<string, unknown> | null = null;
      if (draftSchemaText.trim()) schema = JSON.parse(draftSchemaText);
      setRun(
        await api.testWatch(watch.id, {
          criteria: draftCriteria || undefined,
          extract_schema: schema,
          min_confidence: draftMinConfidence,
          max_price: draftMaxPrice,
          days,
          limit,
          lane_id: laneId === "" ? null : laneId,
          include_stage0_rejects: includeRules,
          only_flagged: onlyFlagged,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ fontSize: 15 }}>Dry run</h2>
        <span className="faint">writes nothing</span>
      </div>

      <div className="row">
        <div style={{ width: 92 }}>
          <label htmlFor="t-days">Days</label>
          <input
            id="t-days"
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        </div>
        <div style={{ width: 92 }}>
          <label htmlFor="t-limit">Listings</label>
          <input
            id="t-limit"
            type="number"
            min={1}
            max={120}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label htmlFor="t-lane">Lane</label>
          <select
            id="t-lane"
            value={laneId}
            onChange={(e) => setLaneId(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">All lanes</option>
            {lanes.map((lane) => (
              <option key={lane.id} value={lane.id}>
                {lane.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <label className="row" style={{ gap: 6, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={includeRules}
            onChange={(e) => setIncludeRules(e.target.checked)}
          />
          Include stage 0 rejects
        </label>
        <label className="row" style={{ gap: 6, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={onlyFlagged}
            onChange={(e) => setOnlyFlagged(e.target.checked)}
          />
          Only flagged misses
        </label>
        <div className="spacer" />
        <button className="primary" onClick={() => void test()} disabled={busy}>
          {busy ? "Classifying..." : "Run"}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {run && (
        <>
          <div className="row" style={{ gap: 8 }}>
            <span className="pill">{run.considered} considered</span>
            <span className="pill pass">{run.would_alert} would alert</span>
            <span className="pill">{run.matched} matched</span>
            {run.changed > 0 && <span className="pill warn">{run.changed} changed</span>}
            <span className="pill mono">{run.model}</span>
          </div>

          {!run.results.length ? (
            <div className="empty">
              No stored listings in that window yet. Let the poller run first.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Listing</th>
                  <th style={{ width: 74 }}>Conf</th>
                  <th style={{ width: 92 }}>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {run.results.map((result) => (
                  <tr key={result.item_id}>
                    <td>
                      <a href={result.url ?? "#"} target="_blank" rel="noreferrer">
                        {result.title}
                      </a>
                      <div className="faint">
                        {money(result.price, result.currency)} - {result.reason}
                      </div>
                      {Object.keys(result.attributes ?? {}).length > 0 && (
                        <div className="attrs">
                          {Object.entries(result.attributes).map(([key, value]) => (
                            <span className="pill" key={key}>
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${confidenceClass(result.confidence)}`}>
                        {Math.round(result.confidence * 100)}%
                      </span>
                    </td>
                    <td>
                      <span className={`pill ${result.would_alert ? "pass" : ""}`}>
                        {result.would_alert ? "alert" : result.matched ? "below bar" : "no"}
                      </span>
                      {result.previous_matched !== null &&
                        result.previous_matched !== result.matched && (
                          <div className="faint">was {result.previous_matched ? "yes" : "no"}</div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
